
import { getLogService } from "@cervval.js/common/src/services/log.service";
import { getProp, setProp } from "@cervval.js/common/src/tools/any.tools";
import { ObjectTools } from "@cervval.js/common/tools";
import { TypeInfos } from "@cervval.js/ts-reflect";
import { FetchDataDefinition } from "./sql.fetchData.types";

export type SqlForeignKey<TableName = string, Column = string> = {

    /** 
     * Foreign table
     * ! table is a ItemValueOrFunction but in current implementation, item is only passed to getItemVOFValue in updateForeignKeys
     */
    table: ObjectTools.ItemValueOrFunction<unknown, TableName>,

    /** Column in foreign's table. By default will use the column with isAutoGeneratedId:true. */
    column?: Column,

    /** 
     * If weak is set to true, the foreign key won't be taken into account when ordering tables.
     * This parameter can be used when you have loop in your foreign keys. The two use cases are :
     * 1) Parenting in the same table
     * 2) Loop in distinct tables
     * 
     * This parameter must only be set to true **if the foreign key is nullable**.
     * Once you have set weak to true on a foreign key, the consequence are :
     * - you will need to create records of the table with the property (the one marked as weak foreign key) set to null,
     * - and then perform an update of the property once all dependencies are created.
     */
    weak?: boolean
};

export interface SqlColumn<TableName = string, FieldTypeName = string> {
    name: string;
    fieldType: FieldTypeName;
    comment?: string;
    isAutoGeneratedId?: boolean;
    isDeletionMarker?: boolean;
    /** do not retrieve column by default (hashed passwords, ...) */
    doNotRetrieve?: boolean;

    /** 1 => is first column in table default "ORDER BY", -1 = isFirst but in DESC order // 2 => is second */
    isDefaultOrderBy?: number;

    isUnique?: boolean;// single column unique constraint
    foreignKey?: SqlForeignKey<TableName>;
    isAssociativeColumn?: boolean; // Not in DB, used when you select data from table with association
}

export interface SqlTableConstraint {
    columnNames: string[];
    keyType: "primary" | "unique";
}

export interface SqlAssociationTable<TableName = string, FieldTypeName = string> extends SqlTableDefinition<TableName, FieldTypeName> {
    /** 
     * when true, SqlHelper.selectFromTable with no association options will NOT return this association data
     * otherwise SqlHelper.selectFromTable will return additional columns
     */
    doNotRetrieveByDefault?: boolean;
    /** column corresponding to the owner id */
    ownerIdColumn: SqlColumn<TableName, FieldTypeName>;
}

export interface SqlJoin<TableName = string> {
    /** Join type - defaults to left */
    joinType?: "left" | "inner";
    /** Column of the "current" table */
    leftColumn: string;
    /** Target table */
    rightTable: TableName;
    /** Alias of the joined table inside the query - by default, it is suffixed by a number ex: rightTable_1 */
    rightTableJoinAlias?: string;
    /** Column in target table - defaults to autoGenerated id column or id */
    rightColumn?: string;
    /** Columns to retrieve in the target column { outputColumnName : rightTableColumnName } 
     * or { outputColumnName : { name : rightTableColumnName, fieldType: specificTypeToUnMarshal }
     * or { outputColumnName : { rawSql : rawSqlString, fieldType: specificTypeToUnMarshal }
     * example: {"__userName": { rawSql: `CONCAT("users_alias"."familyName", ' ', "users_alias"."givenName")`, fieldType: "STRING" } }
     * if no fieldType is set, we take the type of the target column in the target table */
    columns?: Record<string, string | Pick<SqlColumn, "fieldType"> & { name?: string, rawSql?: string }>;
}

export interface SqlTableDefinition<TableName = string, FieldTypeName = string> {
    name: TableName;
    comment?: string;
    columns: SqlColumn<TableName, FieldTypeName>[];

    /** 
     * Primary and unique constraints
     * single column unique constraints can be defined here OR in SqlColumn.isUnique 
     */
    constraints?: SqlTableConstraint[];

    /**
     * Array properties that get values from an associative table
     * string and not TableName since associative tables may not be registered in the list
     */
    associations?: { [propertyName: string]: SqlAssociationTable<string> };

    /** 
     * Columns that get data from another table when using sqlHelper.selectFromTable
     * You define different "JOINs" and the columns you want to retrieve on each joined table
     * 
     * example: you have agentId in table "A" and want to get the agents.familyName and givenName when you load "A"
      A.joinedColumns: [{
       joins: [{
        leftColumn: "agentId", 
        rightTable: "agents", 
        columns: {agentFamilyName : "familyName", agentGivenName : "givenName"}
       }]
      }]
     * 
     * example: if your data is not in a directly linked table, you can pass several joins that are chained
     * In the below example, agents table has a serviceId column
     * (you can still get data from each table in the chain)
      A.joinedColumns: [{
       joins: [{
        joinType : "inner",
        leftColumn: "agentUid",
        rightTable: "agents",
        rightColumn :"uid",
        columns: {agentFamilyName : "familyName", agentGivenName : "givenName"}
       },{
        leftColumn: "serviceId", 
        rightTable: "services", 
        columns: {serviceName : "name"}
       }]
      }]
     * 
     */
    joinedColumns?: { joins: SqlJoin[] }[];

    /**
     * Filter definition for contextual fetchData requests
     * ex : 
     {
        "planning": is<FetchDataDefinition<keyof MyDTO, keyof GetPlanningDataParams>>({
            filters: [
                { type: "intersect_interval", intervalName: "planning", startDate: "startDate", finishDate: "finishDate", mandatory: "both" },
                { type: "=", variableName: "projectId", column: "__projectId", mandatory: true },
                { type: "deletionMarker", minDateVariableName: "deletionDate" }
            ]
        }),
        "admin": is<FetchDataDefinition<keyof MyDTO, keyof GetAdminDataParams>>({
            filters: [
                { type: "IN", variableName: "projectIds", column: "__projectId", mandatory: false }
            ]
        })
    }
     */
    fetchDataDefinitions?: Record<string, FetchDataDefinition>;
}

/**
 * A FieldMarshaller is needed when the data we want to store in the database needs to be converted in order to be persisted
 */
export interface FieldMarshaller<JsonData = unknown, DbData = unknown> {
    /**
     * convert json to db data
     */
    marshal?(obj: JsonData | null | undefined): DbData | null | undefined;

    /**
     * convert db data to json
     */
    unmarshal?(data: DbData | null | undefined): JsonData | null | undefined;
}

// TODO move to data model
export interface StringFieldConstraints {
    maxLength?: number;
}

/**
 * The Sql connectors can expose db or server implementation dependent marshallers
 * This const can be used to reference the UnixTimestamp Marshaller provided by the SqlConnector
 */
export const SQL_CONNECTOR_UNIX_TIMESTAMP_MARSHALLER = "SqlConnector_UnixTimestamp_Marshaller";

/**
 * The Sql connectors can expose db or server implementation dependent marshallers
 * This const can be used to reference the NumberArrayToCSV Marshaller provided by the SqlConnector
 */
export const SQL_CONNECTOR_NUMBER_ARRAY_TO_CSV_MARSHALLER = "SqlConnector_NumberArrayToCSV_Marshaller";

/**
 * The Sql connectors can expose db or server implementation dependent marshallers
 * This const can be used to reference the String Marshaller provided by the SqlConnector
 */
export const SQL_CONNECTOR_STRING_MARSHALLER = "SqlConnector_String_Marshaller";

/**
 * The Sql connectors can expose db or server implementation dependent marshallers
 * This const can be used to reference the TinyMce Marshaller provided by the SqlConnector
 */
export const SQL_CONNECTOR_TINYMCE_MARSHALLER = "SqlConnector_TinyMce_Marshaller";

/**
 * This Marshaller ID can be used to store list of enumerations generated by the dataModelEditor
 */
export const MULTISELECT_MARSHALLER = "Multiselect_Marshaller";

/**
 * Db definitions passed to SqlHelper constructor
 */
export interface SqlDbDefinition<TableName = string, FieldTypeName = string> {

    /**
     * List of all table definitions
     */
    tables: SqlTableDefinition<TableName, FieldTypeName>[];

    /**
     * Marshallers by FieldTypeName, all columns with a specific FieldTypeName will be marshalled / unmarshalled with the appropriate marshaller
     * If a string is provided, the marshaller will be retreived from the sql connector (for marshallers that are db dependent)
     */
    marshallers: Map<FieldTypeName, FieldMarshaller | string>;
}

/**
 * Map of Db tableDefinition by tableName
 */
export type SqlDbDefinitionMap<TableName = string, FieldTypeName = string> = Map<TableName, SqlTableDefinition<TableName, FieldTypeName>>

/**
 * push tableDefinitions found in appElements
 * if (options.checkTableDefinitions) also checks each table definition against provided typeInfos (from DTO)
 */
export function pushTableDefinitions<TableName = string, FieldTypeName = string>(
    tables: SqlTableDefinition<TableName, FieldTypeName>[],
    appElements: Map<TableName, { getTableDefinition: () => SqlTableDefinition<TableName, FieldTypeName>, typeInfos: TypeInfos }>,
    options: { checkTableDefinitions: boolean }): void {
    // gather tables list
    for (const defs of appElements.values()) {
        const table = defs.getTableDefinition();
        if (table) {
            tables.push(table);
        }
        if (options?.checkTableDefinitions) {
            checkTableDefinition(table, defs.typeInfos);
        }
    }
}

/**
 * Checks that table definition is valid and corresponds to the provided dto infos
 * Does not return anything nor throw exception, it simply logs errors
 */
export function checkTableDefinition<TableName = string, FieldTypeName = string>(table: SqlTableDefinition<TableName, FieldTypeName>, dtoInfos: TypeInfos): void {
    const columnsByName = {};

    // check each table column is found and correctly typed
    for (const c of table.columns) {
        if (getProp(columnsByName, c.name)) {
            getLogService().error(table.name, "column", c.name, "was defined twice");
        }
        setProp(columnsByName, c.name, c);

        const prop = dtoInfos.properties?.[c.name];
        if (!prop) {
            getLogService().error(table.name, "column", c.name, "was not found in", dtoInfos.type);
            continue;
        }
        const propType = (prop.declaredType ?? prop.type).replace(" | undefined", "");
        if (String(c.fieldType) !== propType) {
            getLogService().error(table.name, "column", c.name, "field type", c.fieldType, "does not match", dtoInfos.type + "." + c.name, "type", propType);
        }
    }

    // check each DTO property is found
    if (dtoInfos.properties) {
        for (const pName in dtoInfos.properties) {
            if (pName.startsWith("__")) {
                // __ properties are not persisted in the table (it can be cache, a computation result or data coming from another table)
                continue;
            }
            if (getProp(columnsByName, pName) == null && table.associations?.[pName] == null) {
                getLogService().error(dtoInfos.type + "." + pName, "was not found in", table.name);
            }
        }
    }

    // TODO check there is a foreign key for all ID columns that are not auto generated

}

/**
 * @returns the isAutoGeneratedId column name within SqlTableDefinition, fallback to "id"
 */
export function getIdColumn(td: SqlTableDefinition): string {
    for (const col of td.columns) {
        if (col.isAutoGeneratedId) { return col.name; }
    }
    return "id";
}

/**
 * @returns the foreign keys found within SqlTableDefinition
 */
export function getForeignKeys(td: SqlTableDefinition): { [columName: string]: SqlForeignKey<unknown> } {
    const foreignKeys: { [columName: string]: SqlForeignKey<unknown> } = {};
    for (const col of td.columns) {
        if (col.foreignKey) {
            foreignKeys[col.name] = col.foreignKey;
        }
    }
    if (td.associations) {
        for (const col in td.associations) {
            const foreignKey = td.associations[col].ownerIdColumn.foreignKey;
            if (foreignKey) {
                foreignKeys[col] = foreignKey;
            }
        }
    }
    return foreignKeys;
}